import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { OpenShiftsPositionFilterScreenProps } from 'home/stack/types'
import { cloneDeep, isEqual } from 'lodash'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useFiltersContext } from 'schedule/filters/context'
import { MultiSelectSettingView } from 'settings/screens/types/multi_select/view'
import { SettingValue } from 'settings/types'
import Spinner from 'shared/components/spinner'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { useHelperStores } from 'stores/queries/list'

export function OpenShiftsPositionFilterScreen({
  route,
  navigation,
}: OpenShiftsPositionFilterScreenProps) {
  const { theme } = useTheme()

  const storeQuery = useHelperStores()
  const [loading, setLoading] = useState<boolean>(false)

  const {
    currentFilters,
    setCurrentFiltersWithToggle,
    setCurrentSelectedPresetIndex,
  } = useFiltersContext()
  const positionFilter = currentFilters['positions']

  const [selectedIds, setSelectedIds] = useState<readonly string[]>(
    positionFilter.selectedIds
  )
  const [positions, setPositions] = useState<SettingValue[]>(
    positionFilter.values
  )

  const setPositionFilter = useCallback(() => {
    const newPositionFilter = {
      ...positionFilter,
      values: positions,
      selectedIds,
    }
    const newFilters = cloneDeep(currentFilters)
    if (
      !isEqual(
        newFilters['positions'].selectedIds,
        newPositionFilter.selectedIds
      )
    ) {
      newFilters['positions'] = newPositionFilter
      setCurrentFiltersWithToggle(newFilters)
      setCurrentSelectedPresetIndex(undefined)
    }
  }, [
    currentFilters,
    positionFilter,
    positions,
    selectedIds,
    setCurrentFiltersWithToggle,
    setCurrentSelectedPresetIndex,
  ])

  useLayoutEffect(
    () =>
      showHeaderButton({
        navigation,
        theme,
        title: t('done'),
        onPress: async () => {
          setLoading(true)
          setPositionFilter()
          navigation.goBack()
        },
      }),
    [navigation, setPositionFilter, theme]
  )

  useEffect(() => {
    const multiSelectPositions: SettingValue[] = []
    storeQuery.data?.stores.forEach(store => {
      if (store.positions) {
        store.positions.forEach(position => {
          if (!multiSelectPositions.find(pos => pos.id === position.id))
            multiSelectPositions.push({ ...position, locked: false })
        })
      }
    })
    setPositions(Array.from(multiSelectPositions))
  }, [storeQuery.data?.stores])

  return (
    <StatusBoundary
      isLoading={storeQuery.isLoading}
      error={storeQuery.error}
    >
      {loading ? (
        <Spinner />
      ) : (
        <MultiSelectSettingView
          selectedIds={selectedIds as string[]}
          values={positions}
          onSelectValue={setSelectedIds}
        />
      )}
    </StatusBoundary>
  )
}
